/**
 * Czysty opis wizualny ubranka — kolor + kształt Toca-Boca.
 * Używane przez proceduralne tekstury gdy brak PNG item_<id>.
 * Bez Phaser — unit-testowalne.
 */
import { ITEMS, type ItemDef, type OutfitSlot, type SwimSlot } from '@pieskowo/shared';

export type ItemShapeKind =
  | 'dress'
  | 'shirt'
  | 'coat'
  | 'sweater'
  | 'hat'
  | 'crown'
  | 'beret'
  | 'winter_hat'
  | 'party_hat'
  | 'wizard_hat'
  | 'baseball_cap'
  | 'flower_hat'
  | 'bow'
  | 'glasses'
  | 'sun_glasses'
  | 'heart_glasses'
  | 'cat_glasses'
  | 'star_glasses'
  | 'collar'
  | 'bandana'
  | 'pearl_collar'
  | 'shoes'
  | 'boots'
  | 'ballet'
  | 'bag'
  | 'star'
  | 'backpack'
  | 'scarf'
  | 'bowtie'
  | 'wings'
  | 'suit'
  | 'cap'
  | 'generic';

export type ItemVisualSpec = {
  itemId: string;
  slot: OutfitSlot | SwimSlot;
  /** Kolor główny 0xRRGGBB. */
  color: number;
  /** Kolor akcentu (detale). */
  accent: number;
  shape: ItemShapeKind;
  /** Paski / kropki / kwiatki — dekor bez tekstu. */
  pattern: 'none' | 'stripes' | 'dots' | 'flowers' | 'hearts';
};

/** Paleta pasteli per słowo kluczowe w id. */
const COLOR_HINTS: { re: RegExp; color: number; accent: number }[] = [
  { re: /pink|róż/i, color: 0xff9ec8, accent: 0xff6b9d },
  { re: /blue|nieb/i, color: 0x7ec8e3, accent: 0x4a9fc7 },
  { re: /green|ziel/i, color: 0x81c784, accent: 0x4caf50 },
  { re: /yellow|żół|gold|złot/i, color: 0xffe066, accent: 0xf4c430 },
  { re: /red|czer/i, color: 0xff6b6b, accent: 0xe53935 },
  { re: /purple|fiolet|violet/i, color: 0xc4a1ff, accent: 0x9b6bff },
  { re: /heart|serc/i, color: 0xff6b8a, accent: 0xe91e63 },
  { re: /flower|kwiat/i, color: 0xffb6c1, accent: 0xff8fab },
  { re: /star|gwiaz/i, color: 0xffd700, accent: 0xffa000 },
  { re: /sun|słoń/i, color: 0xffcc66, accent: 0xff9800 },
  { re: /rain|deszcz|kalosz/i, color: 0x5dade2, accent: 0x2874a6 },
  { re: /sport/i, color: 0x58d68d, accent: 0x1e8449 },
  { re: /winter|zim/i, color: 0xb3e5fc, accent: 0x4fc3f7 },
  { re: /crown|koron/i, color: 0xffd700, accent: 0xff6b6b },
  { re: /party|imprez/i, color: 0xff8fab, accent: 0x7ec8e3 },
  { re: /stripe|pask/i, color: 0x90caf9, accent: 0xffffff },
  { re: /dot|kropk/i, color: 0xffb6c1, accent: 0xffffff },
  { re: /wing|skrzyd/i, color: 0xe1bee7, accent: 0xffffff },
  { re: /wizard|czarodziej/i, color: 0x7e57c2, accent: 0xffd700 },
  { re: /baseball|bejsbol/i, color: 0x42a5f5, accent: 0xffffff },
  { re: /cat|kot/i, color: 0x6d4c41, accent: 0xffcc80 },
  { re: /pearl|perl/i, color: 0xf5f5f5, accent: 0xe1bee7 },
  { re: /ballet|balet/i, color: 0xff9ec8, accent: 0xff6b9d },
  { re: /boot|botk/i, color: 0x8d6e63, accent: 0x5d4037 },
  { re: /orange|pomara[nń]/i, color: 0xffb74d, accent: 0xff8a65 },
  { re: /rainbow|t[eę]cz/i, color: 0xff8fab, accent: 0x7ec8e3 },
  { re: /bowtie|muszk/i, color: 0x5c6bc0, accent: 0xffffff },
];

const SLOT_DEFAULTS: Record<string, { color: number; accent: number }> = {
  body: { color: 0xff9ec8, accent: 0xff6b9d },
  hat: { color: 0x7ec8e3, accent: 0x4a9fc7 },
  bow: { color: 0xff6b6b, accent: 0xffffff },
  glasses: { color: 0x4a3f55, accent: 0xbde0fe },
  collar: { color: 0xffd700, accent: 0xff6b8a },
  shoes: { color: 0xc4a1ff, accent: 0x9b6bff },
  accessory: { color: 0xb8e6d0, accent: 0x6bc4a6 },
  suit: { color: 0xff69b4, accent: 0xffffff },
  cap: { color: 0x5dade2, accent: 0xffffff },
};

const ITEM_BY_ID = new Map(ITEMS.map((i) => [i.id, i]));

function hashHue(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/** Deterministyczny pastel gdy brak hintów w id. */
function pastelFromId(id: string): { color: number; accent: number } {
  const h = hashHue(id);
  // HSL-ish: pastel via high L
  const r = 140 + (h % 90);
  const g = 140 + ((h >> 8) % 90);
  const b = 140 + ((h >> 16) % 90);
  const color = (r << 16) | (g << 8) | b;
  const accent =
    (((r * 0.75) | 0) << 16) | (((g * 0.75) | 0) << 8) | ((b * 0.75) | 0);
  return { color, accent };
}

export function colorsForItemId(
  itemId: string,
  slot: string,
): { color: number; accent: number } {
  for (const hint of COLOR_HINTS) {
    if (hint.re.test(itemId)) {
      return { color: hint.color, accent: hint.accent };
    }
  }
  const slotDef = SLOT_DEFAULTS[slot];
  if (slotDef) return { ...slotDef };
  return pastelFromId(itemId);
}

export function shapeForItem(itemId: string, slot: OutfitSlot | SwimSlot): ItemShapeKind {
  if (slot === 'suit') return 'suit';
  if (slot === 'cap') return 'cap';
  if (slot === 'bow') return 'bow';
  if (slot === 'glasses') {
    if (/heart|serc/.test(itemId)) return 'heart_glasses';
    if (/sun|słoń/.test(itemId)) return 'sun_glasses';
    if (/cat|kot/.test(itemId)) return 'cat_glasses';
    if (/star|gwiaz/.test(itemId)) return 'star_glasses';
    return 'glasses';
  }
  if (slot === 'collar') {
    if (/bandana|banda/.test(itemId)) return 'bandana';
    if (/pearl|perl/.test(itemId)) return 'pearl_collar';
    return 'collar';
  }
  if (slot === 'shoes') {
    if (/ballet|balet/.test(itemId)) return 'ballet';
    if (/rain|kalosz|boot|botk/.test(itemId)) return 'boots';
    return 'shoes';
  }
  if (slot === 'hat') {
    if (/crown|koron/.test(itemId)) return 'crown';
    if (/beret/.test(itemId)) return 'beret';
    if (/winter|zim/.test(itemId)) return 'winter_hat';
    if (/party|imprez/.test(itemId)) return 'party_hat';
    if (/wizard|czarodziej/.test(itemId)) return 'wizard_hat';
    if (/baseball|bejsbol/.test(itemId)) return 'baseball_cap';
    if (/flower|kwiat/.test(itemId)) return 'flower_hat';
    return 'hat';
  }
  if (slot === 'body') {
    if (/dress|sukien/.test(itemId)) return 'dress';
    if (/coat|rain|płaszcz|raincoat/.test(itemId)) return 'coat';
    if (/sweater|sweter/.test(itemId)) return 'sweater';
    return 'shirt';
  }
  if (slot === 'accessory') {
    if (/wing|skrzyd/.test(itemId)) return 'wings';
    if (/backpack|plecak/.test(itemId)) return 'backpack';
    if (/scarf|szal/.test(itemId)) return 'scarf';
    if (/bowtie|muszk/.test(itemId)) return 'bowtie';
    if (/star|gwiaz/.test(itemId)) return 'star';
    if (/bag|torb/.test(itemId)) return 'bag';
    return 'bag';
  }
  return 'generic';
}

export function patternForItem(itemId: string): ItemVisualSpec['pattern'] {
  if (/stripe|pask/.test(itemId)) return 'stripes';
  if (/dot|kropk/.test(itemId)) return 'dots';
  if (/flower|kwiat/.test(itemId)) return 'flowers';
  if (/heart|serc/.test(itemId)) return 'hearts';
  return 'none';
}

/**
 * Pełna specyfikacja wizualna itemu.
 * Nieznane id (poza katalogiem) — slot z prefiksu lub generic.
 */
export function itemVisualSpec(
  itemId: string,
  item?: ItemDef | null,
): ItemVisualSpec {
  const def = item ?? ITEM_BY_ID.get(itemId) ?? null;
  const slot: OutfitSlot | SwimSlot = def?.slot ?? inferSlotFromId(itemId);
  const { color, accent } = colorsForItemId(itemId, slot);
  return {
    itemId,
    slot,
    color,
    accent,
    shape: shapeForItem(itemId, slot),
    pattern: patternForItem(itemId),
  };
}

function inferSlotFromId(itemId: string): OutfitSlot | SwimSlot {
  if (itemId.startsWith('suit_')) return 'suit';
  if (itemId.startsWith('cap_')) return 'cap';
  if (itemId.startsWith('hat_') || itemId.startsWith('crown')) return 'hat';
  if (itemId.startsWith('bow_')) return 'bow';
  if (itemId.startsWith('glasses_')) return 'glasses';
  if (itemId.startsWith('collar_') || itemId.startsWith('bandana')) return 'collar';
  if (itemId.startsWith('shoes_')) return 'shoes';
  if (itemId.startsWith('acc_')) return 'accessory';
  if (
    itemId.startsWith('dress_') ||
    itemId.startsWith('shirt_') ||
    itemId.startsWith('sweater_') ||
    itemId.startsWith('tshirt_') ||
    itemId.startsWith('raincoat_')
  ) {
    return 'body';
  }
  return 'accessory';
}

/** Każdy item z katalogu ma unikalny, deterministyczny spec. */
export function allCatalogVisualSpecs(
  catalog: readonly ItemDef[] = ITEMS,
): ItemVisualSpec[] {
  return catalog.map((i) => itemVisualSpec(i.id, i));
}

/**
 * Alias assetów gdy plik ma inną nazwę niż id (np. item_bandana.png → bandana_blue).
 * Klucz = itemId, wartość = nazwa pliku bez item_ i .png.
 */
export const ITEM_SPRITE_ALIASES: Readonly<Record<string, string>> = {
  bandana_blue: 'bandana',
  hat_crown: 'hat_crown', // też crown
};

/** Id plików PNG obecnych w /assets/real (uzupełniane w Preload). */
export const KNOWN_ITEM_SPRITE_FILES = [
  'hat_crown',
  'hat_sun',
  'hat_party',
  'dress_pink',
  'dress_flowers',
  'sweater_green',
  'bow_red',
  'glasses_round',
  'glasses_sun',
  'collar_gold',
  'collar_heart',
  'collar_bell',
  'collar_flowers',
  'acc_backpack',
  'acc_wings',
  // dodatkowe pliki w real/ (nazwy ≠ zawsze id katalogu)
  'bandana',
  'bow',
  'crown',
  'glasses',
  'hat',
  'sweater',
] as const;

/** Czy item ma dedykowany PNG (po aliasie). */
export function resolvedSpriteFileId(itemId: string): string | null {
  if ((KNOWN_ITEM_SPRITE_FILES as readonly string[]).includes(itemId)) {
    return itemId;
  }
  const alias = ITEM_SPRITE_ALIASES[itemId];
  if (alias && (KNOWN_ITEM_SPRITE_FILES as readonly string[]).includes(alias)) {
    return alias;
  }
  // partial: crown file for hat_crown already covered
  if (itemId === 'hat_crown') return 'hat_crown';
  return null;
}
